import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Nav, NavItem, NavLink } from "reactstrap";
import TableauViz from "../components/TableauViz";
import { toast } from "react-toastify";
import { getAuthHeaders } from "../utils/auth";

// Backend endpoint for the Direct-Trust JWT that authenticates the
// embedded Tableau viz. Hit the API host directly — a relative
// "/api/tableau/token" falls through the frontend SPA catch-all.
const TABLEAU_TOKEN_URL = "https://api.lssi-fin.com/api/tableau/token";

// Catalog of Tableau views embedded inside LSSI Fin. Add entries here to
// surface a new dashboard — the picker builds itself from this array.
// Salvador owns which views appear. To add one:
//   1. Open the view in Tableau Cloud → click "Ver original" and copy the URL
//   2. Append ?:embed=y&:showAppBanner=false to the copied URL → that goes in `url`
//   3. The `contentUrl` is the part after /views/ WITHOUT query string
//      (e.g. "OnlineSales/Dashboard1"). The backend uses it to scope the JWT.
const DASHBOARDS = [
  {
    id: "online-sales",
    name: "Ventas en Línea",
    description: "Panorama de ventas de cursos online.",
    contentUrl: "OnlineSales/Dashboard1",
    url: "https://us-east-1.online.tableau.com/t/lssi/views/OnlineSales/Dashboard1?:embed=y&:showAppBanner=false",
  },
];

const LAST_DASHBOARD_KEY = "lssi.dashboards.lastSelectedId";

export default function Dashboards() {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(() => {
    try {
      const saved = window.localStorage.getItem(LAST_DASHBOARD_KEY);
      if (saved && DASHBOARDS.some((d) => d.id === saved)) return saved;
    } catch (_) {}
    return DASHBOARDS[0]?.id || "";
  });

  const selected = useMemo(
    () => DASHBOARDS.find((d) => d.id === selectedId) || DASHBOARDS[0],
    [selectedId]
  );

  useEffect(() => {
    if (!selected?.contentUrl) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setToken("");
    (async () => {
      try {
        const headers = getAuthHeaders();
        const { data } = await axios.get(TABLEAU_TOKEN_URL, {
          headers,
          params: { view: selected.contentUrl },
        });
        if (cancelled) return;
        if (!data?.token) throw new Error("No token in response");
        setToken(data.token);
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        toast.error("No pudimos obtener el token de Tableau. Inicia sesión e intenta de nuevo.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected?.contentUrl]);

  const handleSelect = (id) => {
    setSelectedId(id);
    try { window.localStorage.setItem(LAST_DASHBOARD_KEY, id); } catch (_) {}
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
        <div>
          <h2 className="mb-1">Dashboards</h2>
          <small className="text-muted">
            Vistas de Tableau embebidas dentro de LSSI Fin — misma base de datos, sin salir de la app.
          </small>
        </div>
        {DASHBOARDS.length > 1 && (
          <select
            value={selectedId}
            onChange={(e) => handleSelect(e.target.value)}
            className="form-select"
            style={{ maxWidth: 320 }}
          >
            {DASHBOARDS.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        )}
      </div>

      {DASHBOARDS.length > 1 && (
        <Nav tabs className="mb-2">
          {DASHBOARDS.map((d) => (
            <NavItem key={d.id}>
              <NavLink
                href="#"
                active={selectedId === d.id}
                onClick={(e) => { e.preventDefault(); handleSelect(d.id); }}
              >
                {d.name}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      )}

      {selected?.description && (
        <div className="text-muted mb-2" style={{ fontSize: "0.9em" }}>
          {selected.description}
        </div>
      )}

      {loading && <div style={{ padding: 12 }}>Cargando Tableau…</div>}

      {!loading && token && selected && (
        <TableauViz
          key={selected.id}
          src={selected.url}
          token={token}
          toolbar="bottom"
          height="82vh"
        />
      )}

      {!loading && !token && (
        <div style={{ padding: 12 }}>
          <p>No pudimos obtener un token de acceso a Tableau.</p>
          <button
            onClick={() => window.open("https://us-east-1.online.tableau.com", "_blank", "noopener")}
            className="btn btn-primary"
          >
            Iniciar sesión en Tableau Cloud
          </button>
        </div>
      )}

      {DASHBOARDS.length === 1 && (
        <div className="mt-3 text-muted" style={{ fontSize: "0.85em" }}>
          <strong>Nota:</strong> Salvador puede agregar más dashboards editando el array
          <code> DASHBOARDS </code> en <code>src/pages/dashboards.js</code>. Cada entrada
          necesita la URL "Ver original" de Tableau con <code>?:embed=y&amp;:showAppBanner=false</code> al final.
        </div>
      )}
    </div>
  );
}
